import React from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import { ROUTES } from "config/navConfig";
export default function OpportunityHeader({ opportunity, userRole }) {
  const navigate = useNavigate();

  const stageColors = {
    "Diligence": "bg-blue-100 text-blue-800",
    "IC Review": "bg-purple-100 text-purple-800",
    "Screening": "bg-yellow-100 text-yellow-800",
    "Execution": "bg-green-100 text-green-800",
    "Sourcing": "bg-gray-100 text-gray-700",
  };

  const riskColors = {
    "Low": "bg-emerald-100 text-emerald-800",
    "Medium": "bg-amber-100 text-amber-800",
    "High": "bg-red-100 text-red-800",
  };

  return (
    <div className="bg-background border-b border-border px-6 md:px-10 lg:px-12 py-4 md:py-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 mb-4 text-sm font-caption text-muted-foreground" aria-label="Breadcrumb">
        <button onClick={() => navigate(ROUTES.dashboard)} className="hover:text-primary transition-colors">
          Dashboard
        </button>
        <Icon name="ChevronRight" size={14} />
        <button onClick={() => navigate(ROUTES.opportunities)} className="hover:text-primary transition-colors">
          Opportunities
        </button>
        <Icon name="ChevronRight" size={14} />
        <span className="text-foreground font-medium truncate max-w-xs">{opportunity?.name}</span>
      </nav>
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-caption font-medium ${stageColors?.[opportunity?.stage] || "bg-gray-100 text-gray-700"}`}>
              {opportunity?.stage}
            </span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-caption font-medium ${riskColors?.[opportunity?.riskRating] || "bg-gray-100 text-gray-700"}`}>
              {opportunity?.riskRating} Risk
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-caption font-medium bg-primary/10 text-primary">
              <Icon name="Leaf" size={11} />
              {opportunity?.impactTheme}
            </span>
          </div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground mb-1">{opportunity?.name}</h1>
          <div className="flex flex-wrap items-center gap-3 text-sm font-caption text-muted-foreground">
            <span className="flex items-center gap-1">
              <Icon name="MapPin" size={14} />
              {opportunity?.region}
            </span>
            <span className="flex items-center gap-1">
              <Icon name="Building2" size={14} />
              {opportunity?.sector}
            </span>
            <span className="flex items-center gap-1">
              <Icon name="Calendar" size={14} />
              Updated {opportunity?.lastUpdated}
            </span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <Button variant="outline" iconName="ArrowLeft" iconPosition="left" iconSize={16} onClick={() => navigate(ROUTES.opportunities)}>
            Back to List
          </Button>
          {(userRole === "investor" || userRole === "admin") && (
            <Button variant="default" iconName="Plus" iconPosition="left" iconSize={16} onClick={() => navigate("/spv-creation-wizard")}>
              Create SPV
            </Button>
          )}
        </div>
      </div>
      {/* Key Metrics Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 pt-5 border-t border-border">
        {[
          { label: "Target IRR", value: opportunity?.targetIRR, icon: "TrendingUp" },
          { label: "Ticket Size", value: opportunity?.ticketSize, icon: "PoundSterling" },
          { label: "Project Value", value: opportunity?.projectValue, icon: "BarChart3" },
          { label: "Hold Period", value: opportunity?.holdPeriod, icon: "Clock" },
        ]?.map((m) => (
          <div key={m?.label} className="flex items-center gap-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "rgba(59,76,76,0.08)" }}>
              <Icon name={m?.icon} size={16} color="var(--color-primary)" />
            </div>
            <div>
              <p className="font-data text-base md:text-lg font-semibold text-foreground whitespace-nowrap">{m?.value}</p>
              <p className="font-caption text-xs text-muted-foreground">{m?.label}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}